/**
 * SHX header — interaction layer for the custom premium header (sections/shx-header.liquid).
 *
 * Loaded as an ES module: everything here is module-scoped, so nothing collides with
 * Shrine's global classic scripts (secondary.js / remote main.js).
 *
 * Cooperates with Shrine's existing systems instead of replacing them:
 * - Sticky state/classes come from Shrine's <sticky-header> + <sticky-group-manager>.
 * - While any panel is open, `preventHide` is set on the sticky element (Shrine contract)
 *   and the wrapper gets `.shx-open` so CSS can force a readable, solid header state.
 * - The search modal is Shrine's remote-driven <details-modal>; we only observe its state.
 *
 * Robustness rules learned from production:
 * - The drawer's open state is ALWAYS derived from the native dialog (`dialog.open`),
 *   never mirrored in a flag that can go stale.
 * - All drawer animation is class-driven (`.shx-in`) with widely-supported CSS only.
 */

const FINE_POINTER = window.matchMedia('(pointer: fine)');
const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
const DRAWER_FALLBACK_MS = 450;

/** Counts every open header overlay so `.shx-open` survives overlapping open/close. */
const openState = {
  count: 0,
  wrapper() {
    return document.querySelector('.shx-wrap');
  },
  change(delta) {
    this.count = Math.max(0, this.count + delta);
    const wrapper = this.wrapper();
    if (!wrapper) return;
    wrapper.classList.toggle('shx-open', this.count > 0);
    // Shrine contract: StickyHeader checks `.preventHide` on the wrapper element.
    wrapper.preventHide = this.count > 0;
  },
  reset() {
    this.count = 0;
    this.change(0);
  },
};

function publishHeaderBottomPosition() {
  const root = document.documentElement;
  if (root.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
  const wrapper = openState.wrapper();
  if (!wrapper) return;
  root.style.setProperty(
    '--header-bottom-position-desktop',
    `${Math.floor(wrapper.getBoundingClientRect().bottom)}px`
  );
}

/* ------------------------------------------------------------------ */
/* Desktop navigation: dropdowns + mega menus                          */
/* ------------------------------------------------------------------ */

class ShxNav extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    const { signal } = this.abort;

    this.details = Array.from(this.querySelectorAll('details.shx-details'));
    this.headerRow = this.closest('.shx-header');
    this.wrapper = this.closest('.shx-wrap');

    // Escape closes a hover-opened panel even when focus never entered it (WCAG 1.4.13).
    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Escape') return;
        const open = this.details.find((d) => d.open);
        if (!open) return;
        const hadFocus = open.contains(document.activeElement);
        open.open = false;
        if (hadFocus) open.querySelector('summary')?.focus();
      },
      { signal }
    );

    // Shrine's sticky manager hides the header without honoring preventHide in
    // managed mode — close our panels when it announces a hide (stock parity:
    // the stock header closes its menus in onManagedHide the same way).
    this.wrapper?.addEventListener('stickyHide', () => this.closeSiblings(null), { signal });

    this.details.forEach((details) => {
      const summary = details.querySelector('summary');

      details.addEventListener(
        'toggle',
        () => {
          summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
          openState.change(details.open ? 1 : -1);
          if (details.open) {
            publishHeaderBottomPosition();
            this.closeSiblings(details);
            this.armPointerClose(details);
          }
        },
        { signal }
      );

      if (details.dataset.shxTrigger === 'hover') {
        details.addEventListener(
          'pointerenter',
          () => {
            if (FINE_POINTER.matches && !details.open) details.open = true;
          },
          { signal }
        );

        // Hover trigger renders the label as a real link (click navigates,
        // hover opens the panel). Touch has no hover, so there the first tap
        // opens the panel instead; a second tap follows the link.
        const summaryLink = summary.querySelector('[data-shx-summary-link]');
        summaryLink?.addEventListener(
          'click',
          (event) => {
            if (!FINE_POINTER.matches && !details.open) {
              event.preventDefault();
              details.open = true;
            }
          },
          { signal }
        );
      }

      details.addEventListener(
        'keyup',
        (event) => {
          if (event.key === 'Escape' && details.open) {
            details.open = false;
            summary.focus();
          }
        },
        { signal }
      );

      details.addEventListener(
        'focusout',
        () => {
          requestAnimationFrame(() => {
            const active = document.activeElement;
            /* SHX §3.24, second line of defence: when focus lands on <body> it
               means the focused element was removed or disabled, NOT that the
               visitor tabbed away — closing here is what shut the mega menu the
               moment a carousel arrow disabled itself at the end of the strip.
               A real "left the menu" always lands on some other element. */
            if (!active || active === document.body) return;
            if (details.open && !details.contains(active)) {
              details.open = false;
            }
          });
        },
        { signal }
      );
    });

    // Mega-menu promo carousels: hover arrows scroll the strip, visibility tracks position.
    this.querySelectorAll('[data-shx-scroller]').forEach((scroller) => {
      const zone = scroller.closest('.shx-mega__promo-zone');
      if (!zone) return;
      const prev = zone.querySelector('[data-shx-scroll="-1"]');
      const next = zone.querySelector('[data-shx-scroll="1"]');
      const dirFlip = document.body.dataset.rtl === 'true' ? -1 : 1;
      const megaDetails = scroller.closest('details');
      /**
       * SHX §3.19: a control that disables itself while holding focus blurs to
       * <body>, and the panel's focusout handler reads that as "focus left the
       * menu" and closes it. Hand focus to the opposite arrow (or the first
       * promo link) so a keyboard visitor stays inside the open panel.
       */
      /**
       * SHX §3.24 — THE actual cause of "the mega menu closes when I click the
       * carousel arrow".
       *
       * Setting `disabled = true` on a focused button blurs it SYNCHRONOUSLY,
       * so document.activeElement is already <body> by the time this runs. The
       * previous version tested `document.activeElement !== button` here and
       * therefore always bailed out — focus stayed on <body>, the panel's
       * focusout handler saw focus outside the menu, and closed it.
       *
       * The caller must now capture whether the button held focus BEFORE
       * disabling it and pass that in.
       */
      const keepFocusInside = (button, hadFocus) => {
        if (!button || !hadFocus) return;
        const other = button === next ? prev : next;
        const fallback =
          (other && !other.disabled && !other.hidden && other) ||
          scroller.querySelector('a, button:not([disabled])') ||
          megaDetails?.querySelector('summary');
        fallback?.focus?.({ preventScroll: true });
      };

      const update = () => {
        const max = scroller.scrollWidth - scroller.clientWidth;
        const pos = Math.abs(scroller.scrollLeft);
        const scrollable = max > 4;
        // SHX §3.19: at the ends the arrows DIM IN PLACE instead of hiding.
        // Hiding removed the button out from under the visitor's cursor, so the
        // browser fired pointerover on whatever sat behind the panel — which
        // armPointerClose read as "the pointer left the menu" and shut the mega
        // menu the instant you clicked through to the last products.
        // `hidden` is still used when there is nothing to scroll at all, which
        // is settled while the panel opens rather than mid-interaction.
        if (prev) {
          // Capture focus BEFORE disabling — disabling blurs it immediately.
          const prevHadFocus = document.activeElement === prev;
          prev.hidden = !scrollable;
          prev.disabled = !scrollable || pos <= 4;
          if (prev.disabled) keepFocusInside(prev, prevHadFocus);
        }
        if (next) {
          const nextHadFocus = document.activeElement === next;
          next.hidden = !scrollable;
          next.disabled = !scrollable || pos >= max - 4;
          if (next.disabled) keepFocusInside(next, nextHadFocus);
        }
      };
      const step = (dir) =>
        scroller.scrollBy({
          left: dir * dirFlip * scroller.clientWidth * 0.7,
          behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth',
        });
      prev?.addEventListener('click', () => step(-1), { signal });
      next?.addEventListener('click', () => step(1), { signal });
      scroller.addEventListener('scroll', update, { passive: true, signal });
      // Measure once the panel is actually visible (details just opened).
      scroller.closest('details')?.addEventListener(
        'toggle',
        (event) => {
          if (event.target.open) requestAnimationFrame(update);
        },
        { signal }
      );
    });
  }

  disconnectedCallback() {
    this.abort?.abort();
    openState.reset();
  }

  closeSiblings(current) {
    this.details.forEach((other) => {
      if (other !== current && other.open) other.open = false;
    });
  }

  /**
   * Stretch-style close tracking: no timers. Once open, the first pointerover on
   * anything unrelated closes the panel. The header row itself and the wrapper are
   * allowed targets: a full-width mega panel hangs below the header, so the pointer
   * legitimately crosses the header's padding band on its way down into the panel.
   */
  armPointerClose(details) {
    if (!FINE_POINTER.matches || details.dataset.shxTrigger !== 'hover') return;
    /**
     * True while the pointer is still over the open panel (or its trigger row).
     * SHX §3.23: a tolerance band is included because controls deliberately
     * straddle the panel edge — the carousel arrows sit at -1.2rem, so half of
     * each arrow hangs OUTSIDE the panel's own box. Without the band, a pointer
     * resting on that overhang counts as "left the menu".
     */
    const EDGE_TOLERANCE = 32;
    const withinPanel = (event) => {
      const boxes = [details.getBoundingClientRect()];
      const panel = details.querySelector('.shx-mega, .shx-nav__dropdown');
      if (panel) boxes.push(panel.getBoundingClientRect());
      return boxes.some(
        (b) =>
          b.width > 0 &&
          b.height > 0 &&
          event.clientX >= b.left - EDGE_TOLERANCE &&
          event.clientX <= b.right + EDGE_TOLERANCE &&
          event.clientY >= b.top - EDGE_TOLERANCE &&
          event.clientY <= b.bottom + EDGE_TOLERANCE
      );
    };

    /**
     * SHX §3.23: the real root cause of "the menu closes when I click the
     * carousel arrow". pointerover does not only fire when the visitor MOVES —
     * it also fires when the element under a STATIONARY cursor disappears or is
     * disabled, and the browser re-targets to whatever is behind it. Every fix
     * so far treated a symptom (which element vanished); this treats the cause:
     * if the pointer has not actually moved since the last event, it is not a
     * "left the menu" signal, whatever the new target happens to be.
     */
    let lastX = null;
    let lastY = null;
    const pointerMoved = (event) => {
      const moved =
        lastX === null || Math.abs(event.clientX - lastX) > 2 || Math.abs(event.clientY - lastY) > 2;
      lastX = event.clientX;
      lastY = event.clientY;
      return moved;
    };
    const onPointerOver = (event) => {
      const t = event.target;
      // Ignore events the visitor did not cause by moving (see pointerMoved).
      if (!pointerMoved(event)) return;
      if (details.contains(t) || t === this.headerRow || t === this.wrapper) return;
      // SHX §3.19: an element vanishing or disabling itself under the cursor
      // (a carousel arrow at the end of its strip, a lazy image swapping in)
      // makes the browser fire pointerover on whatever is behind it, even though
      // the visitor never moved. Only treat it as "left the panel" when the
      // pointer is genuinely outside the open panel's box.
      if (withinPanel(event)) return;
      document.removeEventListener('pointerover', onPointerOver);
      if (details.open) details.open = false;
    };
    document.addEventListener('pointerover', onPointerOver, { signal: this.abort.signal });
  }
}

/* ------------------------------------------------------------------ */
/* Mobile drawer: native <dialog> + panel stack                        */
/* ------------------------------------------------------------------ */

class ShxDrawer extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    const { signal } = this.abort;

    this.dialog = this.querySelector('dialog');
    this.shell = this.querySelector('.shx-drawer__shell');
    this.section = this.closest('.section-header');
    this.closing = false;
    if (!this.dialog) return;

    // Openers live outside this element (the burger button in the header).
    this.section?.querySelectorAll('[data-shx-drawer-open]').forEach((button) => {
      button.addEventListener('click', () => this.open(button), { signal });
    });

    this.addEventListener(
      'click',
      (event) => {
        const opener = event.target.closest('[data-shx-open-panel]');
        if (opener) {
          this.lastOpener = opener;
          this.setPanel(opener.getAttribute('data-shx-open-panel'), opener);
          return;
        }
        if (event.target.closest('[data-shx-back]')) {
          this.setPanel('main', this.lastOpener);
          return;
        }
        if (event.target.closest('[data-shx-drawer-close]')) {
          this.close();
        }
      },
      { signal }
    );

    // Click on the backdrop (the dialog element itself, outside the shell).
    this.dialog.addEventListener(
      'click',
      (event) => {
        if (event.target === this.dialog) this.close();
      },
      { signal }
    );

    // SHX §3.27: Escape must dismiss the INNERMOST open widget first (WAI-ARIA APG).
    // With the language/currency list open in the drawer footer, Escape used to close the
    // whole drawer and leave the list's aria-expanded stuck at "true". Handled on keydown
    // in the capture phase: preventing the default on the Escape keydown suppresses the
    // dialog's close request, so `cancel` never fires and the drawer survives. The vendor
    // LocalizationForm's own keyup handler still runs afterwards and is idempotent.
    this.dialog.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Escape') return;
        const panel = this.dialog.querySelector(
          'localization-form .disclosure__list-wrapper:not([hidden])'
        );
        const form = panel && panel.closest('localization-form');
        if (!form) return;
        event.preventDefault();
        if (typeof form.hidePanel === 'function') {
          form.hidePanel();
        } else {
          panel.setAttribute('hidden', '');
        }
        const button = form.querySelector('button');
        if (button) {
          button.setAttribute('aria-expanded', 'false');
          button.focus();
        }
      },
      { capture: true, signal }
    );

    // Escape triggers `cancel` — intercept to play the close animation first.
    this.dialog.addEventListener(
      'cancel',
      (event) => {
        event.preventDefault();
        this.close();
      },
      { signal }
    );

    // Single cleanup path: runs no matter WHAT closed the dialog.
    this.dialog.addEventListener('close', () => this.afterClose(), { signal });
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.cleanup();
  }

  /** Open state is always derived from the native dialog — it cannot go stale. */
  get isOpen() {
    return this.dialog ? this.dialog.open === true : false;
  }

  open(trigger) {
    if (this.isOpen || this.closing) return;
    this.trigger = trigger || null;
    this.setPanel('main');
    try {
      this.dialog.showModal();
    } catch (e) {
      return;
    }
    this.lockActive = true;
    document.body.classList.add('overflow-hidden');
    trigger?.setAttribute('aria-expanded', 'true');
    openState.change(1);
    // The open class normally lands on the second animation frame (so the
    // browser commits the off-screen transform first, then transitions in).
    // A timer backstop guarantees the drawer reaches its open position even
    // when animation frames are throttled (background tab, low-power mode) —
    // classList.add is idempotent, so both paths may run.
    const arm = () => this.dialog.classList.add('shx-in');
    if (REDUCED_MOTION.matches) {
      arm();
    } else {
      requestAnimationFrame(() => requestAnimationFrame(arm));
      setTimeout(arm, 80);
    }
  }

  close() {
    if (!this.isOpen || this.closing) return;
    this.closing = true;
    this.dialog.classList.remove('shx-in');
    const finish = () => {
      if (!this.closing) return;
      this.closing = false;
      // Cleanup runs HERE, synchronously — never gambled on the dialog's
      // `close` event, which some environments fail to deliver.
      this.cleanup();
      this.dialog.close();
    };
    if (REDUCED_MOTION.matches) {
      finish();
      return;
    }
    const onEnd = (event) => {
      if (event.target !== this.dialog) return;
      this.dialog.removeEventListener('transitionend', onEnd);
      finish();
    };
    this.dialog.addEventListener('transitionend', onEnd);
    setTimeout(() => {
      this.dialog.removeEventListener('transitionend', onEnd);
      finish();
    }, DRAWER_FALLBACK_MS);
  }

  /** Idempotent post-close cleanup — safe to run from finish() AND afterClose(). */
  cleanup() {
    this.dialog.classList.remove('shx-in');
    if (!this.lockActive) return;
    this.lockActive = false;
    document.body.classList.remove('overflow-hidden');
    this.trigger?.setAttribute('aria-expanded', 'false');
    this.trigger = null;
    openState.change(-1);
  }

  afterClose() {
    // Redundant safety for closes that bypass close() (spec-level paths,
    // form method="dialog" submissions, future browser behaviors).
    this.closing = false;
    this.cleanup();
    // Native dialog restores focus to the opener automatically.
  }

  setPanel(name, opener) {
    if (!this.shell) return;
    this.shell.setAttribute('data-shx-current-panel', name === 'main' ? 'main' : 'sub');
    this.querySelectorAll('[data-shx-panel]').forEach((panel) => {
      panel.setAttribute('data-shx-active', panel.getAttribute('data-shx-panel') === name ? 'true' : 'false');
    });
    this.querySelectorAll('[data-shx-open-panel]').forEach((button) => {
      button.setAttribute('aria-expanded', button.getAttribute('data-shx-open-panel') === name ? 'true' : 'false');
    });
    if (this.isOpen) {
      const active = this.querySelector(`[data-shx-panel="${CSS.escape(name)}"]`);
      // Going back: return focus to the item that opened the sub-panel.
      // Going deeper: focus the panel title so the submenu name is announced.
      const focusTarget =
        name === 'main'
          ? opener || active?.querySelector('a, button')
          : active?.querySelector('.shx-drawer__title') || active?.querySelector('.shx-drawer__back');
      requestAnimationFrame(() => focusTarget?.focus({ preventScroll: true }));
    }
  }
}

/* ------------------------------------------------------------------ */
/* Search modal observation + mobile trigger (remote-driven details-modal) */
/* ------------------------------------------------------------------ */

let lastSearchTrigger = null;

function observeSearchModal() {
  document.querySelectorAll('.shx-search > details').forEach((details) => {
    if (details.dataset.shxObserved) return;
    details.dataset.shxObserved = 'true';
    details.addEventListener('toggle', () => {
      openState.change(details.open ? 1 : -1);
      // Shrine's DetailsModal restores focus to the summary, which is CSS-hidden
      // on mobile when the left trigger opened it — send focus back to the trigger.
      if (!details.open && lastSearchTrigger) {
        const trigger = lastSearchTrigger;
        lastSearchTrigger = null;
        requestAnimationFrame(() => trigger.focus({ preventScroll: true }));
      }
    });
  });

  // Search suggestions (Stretch-style): visible only while the query is empty.
  // A term WITH a link is a plain anchor; a term WITHOUT one fills the input
  // and lets Shrine's remote predictive search take over.
  document.querySelectorAll('.shx-search').forEach((root) => {
    const panel = root.querySelector('[data-shx-search-suggest]');
    if (!panel || panel.dataset.shxWired) return;
    panel.dataset.shxWired = 'true';
    const input = root.querySelector('.search__input');

    const sync = () => {
      panel.hidden = !!input && input.value.trim().length > 0;
    };
    input?.addEventListener('input', sync);
    root.querySelector('form.search')?.addEventListener('reset', () => setTimeout(sync, 0));
    sync();

    panel.addEventListener('click', (event) => {
      const term = event.target.closest('[data-shx-search-term]');
      if (!term || !input) return;
      input.value = term.dataset.shxSearchTerm;
      input.focus();
      input.dispatchEvent(new Event('input', { bubbles: true }));
      sync();
    });
  });

  // The optional left-side mobile search button opens the same modal by
  // activating its (CSS-hidden) summary, so Shrine's remote DetailsModal keeps
  // full ownership of open/close behavior. stopPropagation is load-bearing:
  // DetailsModal.open() arms a document.body click listener during the nested
  // summary click; without it, this same tap would bubble to body and
  // immediately close the modal it just opened.
  document.querySelectorAll('[data-shx-open-search]').forEach((button) => {
    if (button.dataset.shxObserved) return;
    button.dataset.shxObserved = 'true';
    button.addEventListener('click', (event) => {
      event.stopPropagation();
      lastSearchTrigger = button;
      button.closest('.section-header')?.querySelector('.shx-search summary')?.click();
    });
  });
}

/* ------------------------------------------------------------------ */
/* Registration                                                        */
/* ------------------------------------------------------------------ */

if (!customElements.get('shx-nav')) customElements.define('shx-nav', ShxNav);
if (!customElements.get('shx-drawer')) customElements.define('shx-drawer', ShxDrawer);

observeSearchModal();
document.addEventListener('shopify:section:load', (event) => {
  // Only reset when OUR header section was re-rendered — an unrelated section
  // reloading must not clobber the open-state of a live header overlay.
  if (event.target && event.target.querySelector('.shx-wrap')) openState.reset();
  observeSearchModal();
});
